/**
 * The problem editor's current source rows, in the shape the crediting rule
 * reasons about.
 *
 * Shared by the two admin surfaces that must not disagree: the Sources tab,
 * which badges whichever row is the original, and the Statement tab's learner
 * preview, which renders the widget. Both resolve through
 * `resolveOriginalCandidate`, so neither can answer from the raw `isOriginal`
 * column alone — an imported problem's owning row is never *marked*, it wins
 * the slot through the ownership fallback, and a tab reading the column would
 * show no original where the learner sees one.
 */
import {
  type AttributionCandidate,
  type ProblemOwner,
  resolveOriginalCandidate,
  resolveOwner,
} from '@/lib/problems/attribution'
import type { ProblemSource } from '../types'

export function toCandidates(sources: ProblemSource[]): AttributionCandidate[] {
  return sources.map((source) => ({
    id: source.id,
    sourceSlug: source.sourceSlug ?? null,
    name: source.source,
    icon: source.sourceIcon ?? null,
    title: source.title,
    url: source.url ?? null,
    siteUrl: source.sourceSiteUrl ?? null,
    premiumRequired: source.premiumRequired,
    isOriginal: source.isOriginal,
  }))
}

/**
 * The id of the row the learner will see credited as ORIGINAL, or `null` when
 * the problem credits nobody.
 */
export function resolveOriginalSourceId(
  sources: ProblemSource[],
): string | null {
  return resolveOriginalCandidate(toCandidates(sources))?.id ?? null
}

/**
 * Who owns the problem, and whether the publish guard may name them.
 *
 * Here rather than in the metadata section for the same reason
 * `resolveOriginalSourceId` is: the answer has to be the one
 * `buildAttribution` would give, or the pre-publish warning and the learner's
 * credit drift apart with nothing to catch it.
 *
 * Annotated with `ProblemOwner` rather than restating its shape: a restated
 * object type is not a fresh literal at the `return`, so excess-property
 * checking never fires and a field added upstream would be silently dropped
 * here instead of erroring.
 */
export function resolveProblemOwner(sources: ProblemSource[]): ProblemOwner {
  return resolveOwner(toCandidates(sources))
}
